import { useState } from "react";

import {
  Beaker02,
  Browser,
  Copy04,
  Eye,
  ImagePlus,
  Share01,
} from "@hl/base-components/lib/assets/icons/HDS/";
import { AlertOctagon } from "@hl/base-components/lib/assets/icons.generated/HDS/Duotone icons/Alerts & feedback";
import {
  OUTLINE_COLOR,
  SECONDARY_COLOR,
} from "@hl/base-components/lib/theme/button";
import {
  STATUS_COLOR,
  UTILITY_COLOR,
} from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import {
  isGoerliTestnetNetwork,
  isTestnetNetwork,
  networkLookup,
} from "@hl/shared-features/lib/utils/blockExplorer";
import {
  Button,
  Card,
  Flex,
  Group,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useNavigate } from "react-router-dom";
import useMantineMediaQueries from "~/../../base-components/lib/hooks/useMantineMediaQueries";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "~/../../shared-features/lib/features/auth/hooks";
import { useModalStack } from "~/../../shared-features/lib/features/modal";

import { _CollectionType } from "apollo/graphql.generated";
import { ReactComponent as Duplicate } from "assets/icons/copy-fill.svg";
import { MINT_BASE_URL, TOOLS_URL } from "config";
import { BlockchainEntity } from "~/components/BlockchainEntityTitle/types";
import CollectionDuplicateModal from "~/features/collections/sections/ContractCollectionDashboard/menu/CollectionDuplicateModal";
import { CollectorsChoiceCreatorReserves } from "~/features/tokens/sections/ManageToken/modals/CreatorReserves/CollectorsChoiceCreatorReservesModal";
import CreatorReservesModal from "~/features/tokens/sections/ManageToken/modals/CreatorReservesModal";

import EmbedSaleModal from "./modals/EmbedSaleModal";
import ShareSaleModal from "./modals/ShareSaleModal";

interface BlockchainEntityTitleProps {
  blockchainEntity: BlockchainEntity;
}

const ManageTokenHeaderButtons = ({
  blockchainEntity,
}: BlockchainEntityTitleProps) => {
  const theme = useMantineTheme();
  const { isMedium } = useMantineMediaQueries();
  const showEmbed = useFeatureFlags(FEATURE_FLAGS.EMBEDS_SHOW_MODAL);
  const { pushModal } = useModalStack();
  const [isCreatorReservesModalOpen, setIsCreatorReservesModalOpen] =
    useState(false);
  const [
    isCollectorsChoiceCreatorReservesModalOpen,
    setIsCollectorsChoiceCreatorReservesModalOpen,
  ] = useState(false);

  const { chainId } = blockchainEntity;

  const size = blockchainEntity.mintStatus?.total;
  const [duplicateCollectionModalOpened, setDuplicateCollectionModalOpened] =
    useState(false);
  const navigate = useNavigate();

  let isOnTestnetChain = false;
  let isOnGoerliBasedChain = false;
  if (chainId) {
    const network = networkLookup(chainId);
    isOnTestnetChain = isTestnetNetwork(network.type);
    isOnGoerliBasedChain = isGoerliTestnetNetwork(network.type);
  }
  // duplicating a goerli-based (deprecated) edition that is not the first edition causes unintended behaviour
  // as the ME contract doesn't exist on sepolia (duplication from goerli based contracts defaults the new draft to sepolia)
  const nonDuplicable =
    isOnGoerliBasedChain && (blockchainEntity.editionId ?? 0) > 0;

  const mintUrl = MINT_BASE_URL + `/${blockchainEntity.id}`;

  const [openedEmbedModal, embedModalActions] = useDisclosure(false);

  function shareMintPageOnClick() {
    pushModal(
      <ShareSaleModal
        data={{
          entity: "mint",
          mintUrl,
          publicPrice: blockchainEntity.publicPrice,
          chainId: chainId!,
        }}
      />,
      {
        size: "lg",
        title: <Text>Share mint page</Text>,
      }
    );
  }

  const isCollectorChoiceMint =
    blockchainEntity.collectionType === _CollectionType.Series &&
    !blockchainEntity.isReveal;

  const remainingTokens =
    size === 0
      ? null
      : (size || 0) - (blockchainEntity.mintStatus?.minted || 0);

  return (
    <>
      <Group spacing={8}>
        {!isMedium && (
          <Button
            component="a"
            color={SECONDARY_COLOR}
            leftIcon={<Eye />}
            target="_blank"
            href={mintUrl}
          >
            View mint page
          </Button>
        )}

        {remainingTokens !== 0 && (
          <Button
            color={SECONDARY_COLOR}
            leftIcon={<ImagePlus />}
            onClick={() =>
              isCollectorChoiceMint
                ? setIsCollectorsChoiceCreatorReservesModalOpen(true)
                : setIsCreatorReservesModalOpen(true)
            }
          >
            Mint creator reserves
          </Button>
        )}
        {showEmbed && (
          <>
            <Button
              color={SECONDARY_COLOR}
              leftIcon={<Browser height={16} width={16} />}
              onClick={embedModalActions.open}
            >
              Embed mint page
            </Button>
            <EmbedSaleModal
              opened={openedEmbedModal}
              onClose={embedModalActions.close}
              collectionId={blockchainEntity.id}
              entity={"mint"}
            />
          </>
        )}
        <Button
          leftIcon={<Share01 height={16} width={16} />}
          color={SECONDARY_COLOR}
          onClick={shareMintPageOnClick}
        >
          Share mint page
        </Button>
      </Group>
      {isOnTestnetChain && (
        <Card
          style={
            isOnGoerliBasedChain ? { borderColor: STATUS_COLOR.ERROR } : {}
          }
          bg={
            isOnGoerliBasedChain
              ? STATUS_COLOR.ERROR_WASH
              : UTILITY_COLOR.TABLE_ROW_BACKGROUND
          }
          py={12}
          pl={16}
          pr={20}
        >
          <Flex
            direction={{ base: "column", md: "row" }}
            gap={20}
            justify="space-between"
            wrap={"nowrap"}
          >
            <Group spacing={16} noWrap>
              {isOnGoerliBasedChain ? (
                <AlertOctagon
                  width={24}
                  height={24}
                  color={theme.colors.errorStatus[0]}
                  style={{ flexShrink: 0 }}
                />
              ) : (
                <Beaker02 width={24} height={24} style={{ flexShrink: 0 }} />
              )}
              <Stack spacing={0}>
                <Text fw={WEIGHT_BOLD} size="sm">
                  {isOnGoerliBasedChain
                    ? "Goerli testnets are being discontinued."
                    : "Ready to transition from testnet?"}
                </Text>
                <Text size="sm">
                  {isOnGoerliBasedChain
                    ? nonDuplicable
                      ? "Create a collection on Sepolia testnet for continued access."
                      : "Duplicate your collection to Sepolia testnet or another chain for continued access."
                    : "Duplicate this collection and deploy on any mainnet."}
                </Text>
              </Stack>
            </Group>
            {!nonDuplicable && (
              <Button
                w="auto"
                color={OUTLINE_COLOR}
                onClick={() => setDuplicateCollectionModalOpened(true)}
                leftIcon={isOnGoerliBasedChain ? <Copy04 /> : <Duplicate />}
                size="md"
              >
                <Text size="sm" fw={WEIGHT_BOLD}>
                  Duplicate collection
                </Text>
              </Button>
            )}
          </Flex>
        </Card>
      )}
      <CollectionDuplicateModal
        isOpened={duplicateCollectionModalOpened}
        entity={blockchainEntity}
        onClose={() => setDuplicateCollectionModalOpened(false)}
        onFinished={() => navigate(TOOLS_URL.home)}
      />
      {blockchainEntity.collectionType &&
        blockchainEntity.chainId &&
        blockchainEntity.address && (
          <>
            <CreatorReservesModal
              isOpened={isCreatorReservesModalOpen}
              remainingTokens={remainingTokens}
              collection={{
                collectionType: blockchainEntity.collectionType,
                editionId: blockchainEntity.editionId,
                id: blockchainEntity.id,
              }}
              onClose={() => setIsCreatorReservesModalOpen(false)}
            />
            <CollectorsChoiceCreatorReserves
              isOpened={isCollectorsChoiceCreatorReservesModalOpen}
              onClose={() =>
                setIsCollectorsChoiceCreatorReservesModalOpen(false)
              }
              collection={{
                id: blockchainEntity.id,
                address: blockchainEntity.address,
                name: blockchainEntity.details.name,
                chainId: blockchainEntity.chainId,
                collectionType: blockchainEntity.collectionType,
                editionId: blockchainEntity.editionId,
              }}
            />
          </>
        )}
    </>
  );
};

export default ManageTokenHeaderButtons;
